import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  /**
   * ---- Card image policy (requested) ----
   * Product cards should behave consistently:
   * - Rest: show media #1 (index 0)
   * - Hover: show media #2 (index 1)
   * - Leave: return to media #1
   *
   * Variants must still update price/availability/URL, but must NOT drive
   * which image is displayed on the card.
   */

  /** @type {HTMLElement | null} */
  #hoverTarget = null;

  /** @type {boolean} */
  #hoverBound = false;

  /**
   * Debug toggle:
   * Add attribute data-debug-hover on a product-card element to enable logs.
   * Example in console:
   *   document.querySelectorAll('product-card').forEach(el => el.setAttribute('data-debug-hover',''));
   */
  #debug(...args) {
    if (this.hasAttribute('data-debug-hover')) {
      // eslint-disable-next-line no-console
      console.log('[ProductCard hover]', ...args);
    }
  }

  /**
   * Bound handlers to keep correct "this" even if we bind to sub-elements.
   * @param {PointerEvent} event
   */
  #onPointerEnter = (event) => this.previewImage(event);

  /**
   * @param {PointerEvent} event
   */
  #onPointerLeave = (event) => this.resetImage(event);

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Prefer slideshow.slides (public) then fallback to slideshow.refs?.slides (internal).
   * @param {import('slideshow').Slideshow} slideshow
   * @returns {HTMLElement[]}
   */
  #getSlides(slideshow) {
    /** @type {any} */
    const raw = slideshow?.slides ?? slideshow?.refs?.slides ?? [];
    if (Array.isArray(raw)) return raw;
    return Array.from(raw);
  }

  /**
   * Return a de-duplicated list of slides in DOM order using slide-id when present.
   * This helps avoid loop/cloned slides if the slideshow implementation uses them.
   * @param {import('slideshow').Slideshow} slideshow
   * @returns {HTMLElement[]}
   */
  #getBaseSlides(slideshow) {
    const slides = this.#getSlides(slideshow);
    if (!slides.length) return slides;

    const seenIds = new Set();
    /** @type {HTMLElement[]} */
    const base = [];

    for (const slide of slides) {
      const id = slide.getAttribute('slide-id');

      // If slide-id exists, de-dup by it
      if (id) {
        if (seenIds.has(id)) continue;
        seenIds.add(id);
        base.push(slide);
        continue;
      }

      // If no slide-id, keep it (best-effort)
      base.push(slide);
    }

    return base;
  }

  /**
   * Ensure all slides are visible (remove any previous "variant-image" hiding).
   * @param {import('slideshow').Slideshow} slideshow
   */
  #forceAllSlidesVisible(slideshow) {
    const slides = this.#getSlides(slideshow);
    for (const slide of slides) {
      slide.hidden = false;
    }
  }

  /**
   * Select base slide by index (0-based). Uses slide-id when available.
   * @param {import('slideshow').Slideshow} slideshow
   * @param {number} baseIndex
   */
  #selectBaseIndex(slideshow, baseIndex) {
    const baseSlides = this.#getBaseSlides(slideshow);
    const slide = baseSlides[baseIndex];

    if (!slide) {
      this.#debug('No slide at baseIndex', baseIndex, 'baseSlides.length=', baseSlides.length);
      return;
    }

    const id = slide.getAttribute('slide-id');

    if (id) {
      this.#debug('Selecting by slide-id', id, '(baseIndex=', baseIndex, ')');
      slideshow.select({ id }, undefined, { animate: false });
    } else {
      // Fallback: select by numeric index
      this.#debug('Selecting by numeric index', baseIndex);
      slideshow.select(baseIndex, undefined, { animate: false });
    }
  }

  /**
   * Find best hover target:
   * - Prefer ref cardGallery if present
   * - else query [ref="cardGallery"]
   * - else fallback to the product-card element itself
   * @returns {HTMLElement}
   */
  #getHoverTarget() {
    const { cardGallery } = this.refs;
    if (cardGallery instanceof HTMLElement) return cardGallery;

    const queried = this.querySelector('[ref="cardGallery"]');
    if (queried instanceof HTMLElement) return queried;

    return this;
  }

  /**
   * Bind pointerenter/pointerleave for hover preview in a resilient way.
   * Safe even if the theme already wires hover elsewhere (idempotent behavior).
   */
  #bindHover() {
    if (this.#hoverBound) return;

    const target = this.#getHoverTarget();
    this.#hoverTarget = target;

    target.addEventListener('pointerenter', this.#onPointerEnter);
    target.addEventListener('pointerleave', this.#onPointerLeave);

    this.#hoverBound = true;
    this.#debug('Hover bound on', target);
  }

  /**
   * Unbind hover listeners.
   */
  #unbindHover() {
    if (!this.#hoverBound) return;

    if (this.#hoverTarget) {
      this.#hoverTarget.removeEventListener('pointerenter', this.#onPointerEnter);
      this.#hoverTarget.removeEventListener('pointerleave', this.#onPointerLeave);
    }

    this.#debug('Hover unbound');
    this.#hoverTarget = null;
    this.#hoverBound = false;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Bind hover preview (image 1 <-> image 2) for product cards.
    this.#bindHover();

    // Ensure card starts on image 1 and no slides are hidden.
    requestAnimationFrame(() => {
      const { slideshow } = this.refs;
      if (!slideshow) return;
      this.#forceAllSlidesVisible(slideshow);
      this.#selectBaseIndex(slideshow, 0);
    });

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.#unbindHover();
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    // Card image policy: variants must NOT drive card image.
    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Variant image filtering DISABLED for product cards.
   * Keep everything visible and reset to image 1.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    this.#forceAllSlidesVisible(slideshow);
    this.#selectBaseIndex(slideshow, 0);
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }

  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * Card policy: do not change the card image for variant previews.
   * (We keep this method so any existing wiring doesn't break.)
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    // Intentionally does not select variant media on cards
    this.resetVariant.cancel();
    this.#debug('previewVariant ignored for card image policy', id);
  }

  /**
   * Hover: always show image 2 (base index 1).
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;
    if (!slideshow) return;

    this.resetVariant.cancel();
    this.#forceAllSlidesVisible(slideshow);

    const baseSlides = this.#getBaseSlides(slideshow);
    if (baseSlides.length < 2) {
      this.#debug('No second image available (baseSlides.length < 2)');
      return;
    }

    this.#selectBaseIndex(slideshow, 1);
    setTimeout(() => this.#preloadNextPreviewImage());
  }

  /**
   * Leave: always return to image 1 (base index 0).
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;
    if (!slideshow) return;

    this.#forceAllSlidesVisible(slideshow);
    this.#selectBaseIndex(slideshow, 0);
  }

  /**
   * Reset variant preview.
   * Card policy: always reset to image 1, never to variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    this.#forceAllSlidesVisible(slideshow);
    this.#selectBaseIndex(slideshow, 0);
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
